import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import useLanguageContext from '../../hooks/useLanguageContext';
import SupportedLanguages from '../../setup/intl/enums';
import { IconBox, IconOrders, IconPlus, IconSearch, IconTick } from '../../assets/svg/icons';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import ScCard from '../../components/atoms/ScCard';
import ScButton from '../../components/atoms/ScButton';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import { Brand, Product, SortKey } from './types';
import { iqd } from './format';
import { unitForms } from './liveData';
import { stockState } from './availability';
import { BrandTile, CartButton, ProductImage, StarRow } from './components';
import {
  StyledAddBtn,
  StyledBadgeTL,
  StyledBadgeTR,
  StyledBrandButton,
  StyledBrandCheck,
  StyledBrandGrid,
  StyledBrandMeta,
  StyledBrandName,
  StyledCardBody,
  StyledCardImage,
  StyledCardName,
  StyledCatPill,
  StyledCatPills,
  StyledClearBrand,
  StyledEmpty,
  StyledErrorState,
  StyledGrid,
  StyledHero,
  StyledHeroEyebrow,
  StyledHeroInputWrap,
  StyledHeroSearchRow,
  StyledHeroTitle,
  StyledMetaLine,
  StyledPriceBig,
  StyledPriceLine,
  StyledPriceRange,
  StyledRatingRow,
  StyledSkeletonBlock,
  StyledSkeletonCard,
  StyledSortSelect,
  StyledSortWrap,
  StyledStock,
  StyledStripHeader,
  StyledToolbar,
  StyledTrendingChip,
  StyledTrendingLabel,
  StyledTrendingRow,
} from './styles';

interface ScreenHomeProps {
  products: Product[];
  brands: Brand[];
  categories: string[];
  categoryLabel: (c: string, lang?: SupportedLanguages) => string;
  cartCount: number;
  cart: Record<string, number>;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpenProduct: (id: string) => void;
  onAddToCart: (p: Product) => void;
  onOpenCart: () => void;
  onOpenOrders: () => void;
  search: string;
  onSearch: (q: string) => void;
  sort: SortKey;
  onSort: (s: SortKey) => void;
  cat: string;
  onCat: (c: string) => void;
  brandFilter: string | null;
  onBrandFilter: (b: string | null) => void;
}

const SORT_OPTIONS: { value: SortKey; id: string; def: string }[] = [
  { value: 'popular', id: 'marketplace.sort.popular', def: 'Popular' },
  { value: 'price_asc', id: 'marketplace.sort.priceAsc', def: 'Price ↑' },
  { value: 'price_desc', id: 'marketplace.sort.priceDesc', def: 'Price ↓' },
  { value: 'rating', id: 'marketplace.sort.rating', def: 'Rating' },
];

const TRENDING: { q: string; labelAr: string }[] = [
  { q: 'Cooking oil', labelAr: 'زيت طبخ' },
  { q: 'Spring Water', labelAr: 'مياه معدنية' },
  { q: 'Laundry powder', labelAr: 'مسحوق غسيل' },
  { q: 'Tissues', labelAr: 'مناديل' },
];

const ScreenHome: React.FC<ScreenHomeProps> = ({
  products,
  brands,
  categories,
  categoryLabel,
  cartCount,
  cart,
  loading,
  error,
  onRetry,
  onOpenProduct,
  onAddToCart,
  onOpenCart,
  onOpenOrders,
  search,
  onSearch,
  sort,
  onSort,
  cat,
  onCat,
  brandFilter,
  onBrandFilter,
}) => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const isAr = language === SupportedLanguages.Arabic;
  const cur = intl.formatMessage({ id: 'marketplace.currency', defaultMessage: 'IQD' });
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => setSearchDraft(search), [search]);

  return (
    <>
      <ScPageHeader
        eyebrow={intl.formatMessage({ id: 'marketplace.eyebrow', defaultMessage: 'Wholesale sourcing' })}
        title={intl.formatMessage({ id: 'mainNavigation.marketplace', defaultMessage: 'Marketplace' })}
        subtitle={intl.formatMessage({ id: 'marketplace.subtitle', defaultMessage: 'Buy products in bulk from verified suppliers — ship straight into your Sandoog warehouse.' })}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ScButton kind={ButtonKind.Ghost} icon={IconOrders} onClick={onOpenOrders}>
              {intl.formatMessage({ id: 'marketplace.orders.nav', defaultMessage: 'My Orders' })}
            </ScButton>
            <CartButton
              count={cartCount}
              onClick={onOpenCart}
              label={intl.formatMessage({ id: 'marketplace.cart.label', defaultMessage: 'Cart' })}
            />
          </div>
        }
      />

      {/* Hero search band */}
      <StyledHero>
        <svg viewBox="0 0 200 200" style={{ position: 'absolute', insetInlineEnd: -10, top: -30, width: 260, height: 260, opacity: 0.16, pointerEvents: 'none' }} aria-hidden="true">
          <g fill="none" stroke="var(--brand-green)" strokeWidth="2">
            <path d="M40,80 L100,40 L160,80 L100,120 Z" />
            <path d="M40,80 L40,150 L100,190 L100,120" />
            <path d="M160,80 L160,150 L100,190" />
          </g>
        </svg>
        <StyledHeroEyebrow>
          {intl.formatMessage(
            { id: 'marketplace.hero.stat', defaultMessage: '{p} products · {b} brands' },
            { p: products.length, b: brands.length }
          )}
        </StyledHeroEyebrow>
        <StyledHeroTitle>{intl.formatMessage({ id: 'marketplace.hero.title', defaultMessage: 'Source your next bestseller' })}</StyledHeroTitle>
        <StyledHeroSearchRow onSubmit={(e) => { e.preventDefault(); onSearch(searchDraft); }}>
          <StyledHeroInputWrap>
            <IconSearch aria-hidden="true" />
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder={intl.formatMessage({ id: 'marketplace.hero.searchPlaceholder', defaultMessage: 'Search products, brands or categories…' })}
              aria-label={intl.formatMessage({ id: 'marketplace.hero.searchPlaceholder', defaultMessage: 'Search products, brands or categories…' })}
            />
          </StyledHeroInputWrap>
          <ScButton kind={ButtonKind.Primary} size={ButtonSize.Large} type="submit" icon={IconSearch}>
            {intl.formatMessage({ id: 'marketplace.hero.search', defaultMessage: 'Search' })}
          </ScButton>
        </StyledHeroSearchRow>
        <StyledTrendingRow>
          <StyledTrendingLabel>{intl.formatMessage({ id: 'marketplace.hero.trending', defaultMessage: 'Trending:' })}</StyledTrendingLabel>
          {TRENDING.map((t) => (
            <StyledTrendingChip key={t.q} type="button" onClick={() => onSearch(isAr ? t.labelAr : t.q)}>{isAr ? t.labelAr : t.q}</StyledTrendingChip>
          ))}
        </StyledTrendingRow>
      </StyledHero>

      {/* Brands strip */}
      <StyledStripHeader>
        <div className="eyebrow">{intl.formatMessage({ id: 'marketplace.shopByBrand', defaultMessage: 'Shop by brand' })}</div>
        {brandFilter && (
          <StyledClearBrand type="button" onClick={() => onBrandFilter(null)}>
            {intl.formatMessage({ id: 'marketplace.clearBrand', defaultMessage: 'Clear brand · {name}' }, { name: brandFilter })}
          </StyledClearBrand>
        )}
      </StyledStripHeader>
      <StyledBrandGrid>
        {brands.map((b) => {
          const active = brandFilter === b.name;
          return (
            <StyledBrandButton
              key={b.id}
              $active={active}
              type="button"
              onClick={() => onBrandFilter(active ? null : b.name)}
            >
              {b.verified && (
                <StyledBrandCheck title={intl.formatMessage({ id: 'marketplace.verifiedSupplier', defaultMessage: 'Verified supplier' })}>
                  <IconTick />
                </StyledBrandCheck>
              )}
              <BrandTile brand={b} />
              <StyledBrandName>{b.name}</StyledBrandName>
              <StyledBrandMeta>{intl.formatMessage({ id: 'marketplace.brandItems', defaultMessage: '{n, plural, one {# product} other {# products}}' }, { n: b.items })}</StyledBrandMeta>
            </StyledBrandButton>
          );
        })}
      </StyledBrandGrid>

      {/* Category + sort toolbar */}
      <StyledToolbar>
        <StyledCatPills>
          {categories.map((c) => (
            <StyledCatPill key={c} $active={cat === c} type="button" onClick={() => onCat(c)}>{categoryLabel(c, language)}</StyledCatPill>
          ))}
        </StyledCatPills>
        <StyledSortWrap>
          <span className="muted" style={{ fontSize: 'var(--t-sm)' }}>
            {intl.formatMessage({ id: 'marketplace.productCount', defaultMessage: '{n} products' }, { n: products.length })}
          </span>
          <StyledSortSelect value={sort} onChange={(e) => onSort(e.target.value as SortKey)} aria-label={intl.formatMessage({ id: 'marketplace.sortBy', defaultMessage: 'Sort by' })}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{intl.formatMessage({ id: o.id, defaultMessage: o.def })}</option>
            ))}
          </StyledSortSelect>
        </StyledSortWrap>
      </StyledToolbar>

      {/* Product grid */}
      <StyledGrid>
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <StyledSkeletonCard key={i} aria-hidden="true">
              <StyledSkeletonBlock $h="180px" />
              <StyledSkeletonBlock $h="0.75rem" $w="50%" />
              <StyledSkeletonBlock $h="1rem" $w="80%" />
              <StyledSkeletonBlock $h="1.25rem" $w="40%" />
              <StyledSkeletonBlock $h="2.25rem" $w="85%" />
            </StyledSkeletonCard>
          ))
        ) : error ? (
          <StyledErrorState role="alert">
            <span>{intl.formatMessage({ id: 'marketplace.loadFailed', defaultMessage: "Couldn't load products. Please try again." })}</span>
            <ScButton kind={ButtonKind.Outline} onClick={onRetry}>{intl.formatMessage({ id: 'marketplace.retry', defaultMessage: 'Retry' })}</ScButton>
          </StyledErrorState>
        ) : products.length === 0 ? (
          <StyledEmpty>
            <span>{intl.formatMessage({ id: 'marketplace.noMatch', defaultMessage: 'No products match these filters' })}</span>
            <ScButton kind={ButtonKind.Outline} onClick={() => { onCat('All'); onBrandFilter(null); onSearch(''); }}>
              {intl.formatMessage({ id: 'marketplace.clearFilters', defaultMessage: 'Clear filters' })}
            </ScButton>
          </StyledEmpty>
        ) : (
          products.map((p) => {
            const name = isAr ? p.nameAr || p.name : p.name;
            const inCart = (cart[p.id] || 0) > 0;
            const lowest = p.tiers[p.tiers.length - 1]?.price ?? p.tiers[0]?.price ?? 0;
            const top = p.tiers[0]?.price ?? 0;
            const ss = stockState(p);
            const soldOut = ss === 'sold-out';
            const uForms = unitForms(p.stockUnit, language);
            return (
              <ScCard
                key={p.id}
                pad={false}
                style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                onClick={() => onOpenProduct(p.id)}
              >
                <StyledCardImage $hue={p.hue}>
                  <ProductImage product={p} />
                  <StyledBadgeTL><Badge tone={BadgeTone.Ink}>{p.brand}</Badge></StyledBadgeTL>
                  {soldOut ? (
                    <StyledBadgeTR><Badge tone={BadgeTone.Neutral}>{intl.formatMessage({ id: 'marketplace.stock.soldOut', defaultMessage: 'Sold out' })}</Badge></StyledBadgeTR>
                  ) : inCart ? (
                    <StyledBadgeTR><Badge tone={BadgeTone.Success} dot>{intl.formatMessage({ id: 'marketplace.inCart', defaultMessage: 'In cart' })}</Badge></StyledBadgeTR>
                  ) : null}
                </StyledCardImage>
                <StyledCardBody>
                  {(p.rating > 0 || p.sold) && (
                    <StyledRatingRow>
                      {p.rating > 0 && (
                        <>
                          <StarRow r={p.rating} />
                          <span>({p.reviews})</span>
                        </>
                      )}
                      {p.sold && <span style={{ marginInlineStart: 'auto' }}>{intl.formatMessage({ id: 'marketplace.sold', defaultMessage: '{n} sold' }, { n: p.sold })}</span>}
                    </StyledRatingRow>
                  )}
                  <StyledCardName>{name}</StyledCardName>
                  <StyledPriceLine>
                    <StyledPriceBig className="mono tnum">{iqd(lowest)}</StyledPriceBig>
                    {lowest !== top && <StyledPriceRange className="mono tnum">– {iqd(top)}</StyledPriceRange>}
                    <StyledPriceRange className="mono tnum">{cur}</StyledPriceRange>
                  </StyledPriceLine>
                  <StyledMetaLine>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} className="tnum"><IconBox />{intl.formatMessage({ id: 'marketplace.moqShort', defaultMessage: 'MOQ {n}' }, { n: p.moq })}</span>
                    {p.lead && <><span>·</span><span>{p.lead}</span></>}
                  </StyledMetaLine>
                  <StyledStock $low={ss === 'low'} className="tnum">
                    {ss === 'unknown'
                      ? intl.formatMessage({ id: 'marketplace.stock.onRequest', defaultMessage: 'Availability on request' })
                      : ss === 'sold-out'
                      ? intl.formatMessage({ id: 'marketplace.stock.soldOut', defaultMessage: 'Sold out' })
                      : ss === 'low'
                      ? intl.formatMessage({ id: 'marketplace.stock.low', defaultMessage: '{n, plural, one {Only # {unit} left} other {Only # {unitPlural} left}}' }, { n: p.stock, ...uForms })
                      : intl.formatMessage({ id: 'marketplace.stock.available', defaultMessage: '{n, plural, one {# {unit} available} other {# {unitPlural} available}}' }, { n: p.stock, ...uForms })}
                  </StyledStock>
                  <StyledAddBtn
                    type="button"
                    $inCart={inCart}
                    $soldOut={soldOut}
                    disabled={soldOut}
                    aria-label={soldOut ? intl.formatMessage({ id: 'marketplace.stock.soldOutAria', defaultMessage: '{name} is sold out' }, { name }) : undefined}
                    onClick={(e) => { e.stopPropagation(); if (!soldOut) onAddToCart(p); }}
                  >
                    {soldOut ? (
                      intl.formatMessage({ id: 'marketplace.stock.soldOut', defaultMessage: 'Sold out' })
                    ) : inCart ? (
                      <><IconTick />{intl.formatMessage({ id: 'marketplace.added', defaultMessage: 'Added' })}</>
                    ) : (
                      <><IconPlus />{intl.formatMessage({ id: 'marketplace.addToCart', defaultMessage: 'Add to cart' })}</>
                    )}
                  </StyledAddBtn>
                </StyledCardBody>
              </ScCard>
            );
          })
        )}
      </StyledGrid>
    </>
  );
};

export default ScreenHome;
